import { useState } from 'react'
import { Zap } from 'lucide-react'
import { Button } from './button'

/**
 * "Récupérer le code (démo)" — the way past an OTP screen in development.
 *
 * <p>Codes are hashed at rest, so nobody can read one back; that is correct,
 * and it also means a developer with no access to the phone number they typed
 * cannot finish a signup, a phone change or a staff sign-in. This button asks
 * the development-only endpoint that remembers the last code per destination
 * and fills it in.
 *
 * <p>The guard is `available`, which every caller passes from
 * `devCodeAvailable()` — a build-mode check, not a runtime flag someone can
 * flip. In production the component renders nothing and the route it would
 * call does not exist.
 */
export function DevCodeButton({
  available,
  destination,
  since,
  fetchCode,
  onCode,
  className,
}: {
  available: boolean
  /** The phone number or e-mail the code went to. */
  destination: string
  /**
   * When this screen asked for the code, in epoch milliseconds.
   *
   * <p>The log keeps the last code per destination, so on a resend — or a
   * second sign-in with the same account — the previous, already-consumed code
   * is still sitting there and comes back instantly. Passing the moment of the
   * send makes the lookup wait for one delivered after it.
   */
  since?: number
  fetchCode: (destination: string, notBefore?: number) => Promise<string | null>
  onCode: (code: string) => void
  className?: string
}) {
  const [busy, setBusy] = useState(false)
  const [missing, setMissing] = useState(false)

  if (!available) return null

  return (
    <div className={className}>
      <Button
        type="button"
        variant="outline"
        block
        disabled={busy || destination.trim() === ''}
        data-testid="dev-fill-code"
        onClick={() => {
          setMissing(false)
          setBusy(true)
          void fetchCode(destination, since)
            .then((code) => {
              if (code) onCode(code)
              else setMissing(true)
            })
            .finally(() => setBusy(false))
        }}
      >
        <Zap className="size-4" aria-hidden="true" />
        {busy ? 'Recherche du code…' : 'Récupérer le code (démo)'}
      </Button>
      {missing ? (
        <p className="mt-2 text-center text-xs text-slate-500">
          Aucun code livré à {destination}. Renvoyez-en un, puis réessayez.
        </p>
      ) : null}
      <p className="text-2xs mt-1 text-center text-slate-400">
        Disponible uniquement en développement.
      </p>
    </div>
  )
}
